import { useSyncExternalStore } from 'react';

/* Nothing in this app fails without saying so (V6 section J).
 *
 * The audit that opened the round found sixty-one places where a request
 * could fail and leave no trace: about forty-five `.catch(() => undefined)`
 * swallows, twenty promises with no catch at all, and the fetch layer
 * itself flattening every transport error into one string. The effect was
 * that a stale list, a reorder that silently reverted, an autosave that
 * never happened and a working app all looked exactly alike.
 *
 * Some failures belong inline, next to the control that failed, and those
 * are handled where they happen. This is for the rest: the background
 * reads a screen does on your behalf. They surface as a small stack of
 * dismissible notes rather than vanishing, and identical failures collapse
 * into one line with a count so a thirty-second poll cannot bury the
 * screen.
 */
export interface Report {
  id: number;
  what: string;
  message: string;
  count: number;
  at: number;
}

let seq = 1;
let reports: Report[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function errorText(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = String((err as { message?: unknown }).message ?? '');
    if (m) return m;
  }
  return 'The request did not go through.';
}

export function report(what: string, err: unknown): void {
  const message = errorText(err);
  const existing = reports.find((r) => r.what === what && r.message === message);
  if (existing) {
    existing.count += 1;
    existing.at = Date.now();
    reports = [...reports];
  } else {
    // A cap, so a screen polling every thirty seconds cannot stack up.
    reports = [...reports.slice(-4), { id: seq++, what, message, count: 1, at: Date.now() }];
  }
  emit();
}

/** `.catch(reporter('the job list'))` — the one-liner the sweep uses. */
export const reporter = (what: string) => (err: unknown) => {
  report(what, err);
};

export function dismissReport(id: number): void {
  reports = reports.filter((r) => r.id !== id);
  emit();
}

export function dismissAllReports(): void {
  if (!reports.length) return;
  reports = [];
  emit();
}

export function useReports(): Report[] {
  return useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => reports,
    () => reports,
  );
}
